import {
  AT_HOME_SITUATION,
  GUARDIAN,
  HOME_CARE_SITUATION,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
} from "core/consts";
import {
  AHBfloorSchema,
  AHBpostleitzahlSchema,
  createMaxLengthMessage,
  createMinLengthMessage,
  IKNumberSchema,
  predicamentSchema,
  siteIdSchema,
  unixDateSchema,
} from "core/schemas/shared";
import { GKVGeneralFormData } from "core/types";
// TODO: move createSimultaneousSchema to core/schemas
// eslint-disable-next-line no-outside-core-imports/no-outside-core-imports
import { createSimultaneousSchema } from "dsl/ecosystems/PatientAssessment/schemas/utils";
import Translations from "translations/types";
import { z } from "zod";
import { isFilled, stripEmptyFields } from "./utils";

export const GUARDIAN_1 = 1 as const;
export const GUARDIAN_2 = 2 as const;
export const FIELD_HAS_VALUE = "FIELD_HAS_VALUE" as const;

// Field length constants
export const FIELD_LIMITS = {
  CASE_ID: 15,
  CITY: 40,
  DESCRIPTION_LONG: 600,
  DESCRIPTION_SHORT: 150,
  HOUSE_NUMBER: 9,
  INSURANCE_NUMBER_MAX: 12,
  INSURANCE_NUMBER_MIN: 10,
  NAME: 45,
  PHONE: 25,
  STREET: 46,
  ZIPCODE: 10,
} as const;

/**
 * Creates guardian fields dynamically for the specified guardian number
 */
const createGuardianFields = (translations: Translations) => {
  return Object.fromEntries(
    [GUARDIAN_1, GUARDIAN_2].flatMap((guardianNum) => [
      [
        `insured_details_guardian_${guardianNum}_type`,
        z.nativeEnum(GUARDIAN).nullish(),
      ],
      [
        `insured_details_guardian_${guardianNum}_first_name`,
        z
          .string()
          .max(
            FIELD_LIMITS.NAME,
            createMaxLengthMessage(translations, FIELD_LIMITS.NAME),
          )
          .nullish(),
      ],
      [
        `insured_details_guardian_${guardianNum}_last_name`,
        z
          .string()
          .max(
            FIELD_LIMITS.NAME,
            createMaxLengthMessage(translations, FIELD_LIMITS.NAME),
          )
          .nullish(),
      ],
      [
        `insured_details_guardian_${guardianNum}_street`,
        z
          .string()
          .max(
            FIELD_LIMITS.STREET,
            createMaxLengthMessage(translations, FIELD_LIMITS.STREET),
          )
          .nullish(),
      ],
      [
        `insured_details_guardian_${guardianNum}_house_number`,
        z
          .string()
          .max(
            FIELD_LIMITS.HOUSE_NUMBER,
            createMaxLengthMessage(translations, FIELD_LIMITS.HOUSE_NUMBER),
          )
          .nullish(),
      ],
      [
        `insured_details_guardian_${guardianNum}_zipcode`,
        z
          .string()
          .max(
            FIELD_LIMITS.ZIPCODE,
            createMaxLengthMessage(translations, FIELD_LIMITS.ZIPCODE),
          )
          .nullish(),
      ],
      [
        `insured_details_guardian_${guardianNum}_city`,
        z
          .string()
          .max(
            FIELD_LIMITS.CITY,
            createMaxLengthMessage(translations, FIELD_LIMITS.CITY),
          )
          .nullish(),
      ],
      [
        `insured_details_guardian_${guardianNum}_phone`,
        z
          .string()
          .max(
            FIELD_LIMITS.PHONE,
            createMaxLengthMessage(translations, FIELD_LIMITS.PHONE),
          )
          .nullish(),
      ],
    ]),
  );
};

function createBaseGKVGeneralFormSchema(
  translations: Translations,
  isRedesignActive: boolean,
) {
  return z.object({
    domestic_situation_is_safe: z.preprocess(
      (val) => (val == 0 ? null : val),
      predicamentSchema.nullish(),
    ),
    general_practitioner_city: z
      .string()
      .max(
        FIELD_LIMITS.CITY,
        createMaxLengthMessage(translations, FIELD_LIMITS.CITY),
      )
      .nullish(),
    general_practitioner_contact_details: z
      .string()
      .max(
        FIELD_LIMITS.DESCRIPTION_LONG,
        createMaxLengthMessage(translations, FIELD_LIMITS.DESCRIPTION_LONG),
      )
      .nullish(),
    general_practitioner_first_name: z
      .string()
      .max(
        FIELD_LIMITS.NAME,
        createMaxLengthMessage(translations, FIELD_LIMITS.NAME),
      )
      .nullish(),
    general_practitioner_honorific_prefix: z
      .string()
      .max(
        FIELD_LIMITS.NAME,
        createMaxLengthMessage(translations, FIELD_LIMITS.NAME),
      )
      .nullish(),
    general_practitioner_honorific_suffix: z
      .string()
      .max(
        FIELD_LIMITS.NAME,
        createMaxLengthMessage(translations, FIELD_LIMITS.NAME),
      )
      .nullish(),
    general_practitioner_housenumber: z
      .string()
      .max(
        FIELD_LIMITS.HOUSE_NUMBER,
        createMaxLengthMessage(translations, FIELD_LIMITS.HOUSE_NUMBER),
      )
      .nullish(),
    general_practitioner_job_title: z
      .string()
      .max(
        FIELD_LIMITS.DESCRIPTION_SHORT,
        createMaxLengthMessage(translations, FIELD_LIMITS.DESCRIPTION_SHORT),
      )
      .nullish(),
    general_practitioner_last_name: z
      .string()
      .max(
        FIELD_LIMITS.NAME,
        createMaxLengthMessage(translations, FIELD_LIMITS.NAME),
      )
      .nullish(),
    general_practitioner_name: z
      .string()
      .max(
        FIELD_LIMITS.NAME,
        createMaxLengthMessage(translations, FIELD_LIMITS.NAME),
      )
      .nullish(),
    general_practitioner_phone_number: z
      .string()
      .max(
        FIELD_LIMITS.PHONE,
        createMaxLengthMessage(translations, FIELD_LIMITS.PHONE),
      )
      .nullish(),
    general_practitioner_street: z
      .string()
      .max(
        FIELD_LIMITS.STREET,
        createMaxLengthMessage(translations, FIELD_LIMITS.STREET),
      )
      .nullish(),
    general_practitioner_zipcode: AHBpostleitzahlSchema(translations).nullish(),
    hospital_ik_number: IKNumberSchema.nullish(),
    // Section I
    insurance_ik_number: IKNumberSchema.nullish(),
    insurance_number: z
      .string()
      .min(
        FIELD_LIMITS.INSURANCE_NUMBER_MIN,
        createMinLengthMessage(translations, FIELD_LIMITS.INSURANCE_NUMBER_MIN),
      )
      .max(
        FIELD_LIMITS.INSURANCE_NUMBER_MAX,
        createMaxLengthMessage(translations, FIELD_LIMITS.INSURANCE_NUMBER_MAX),
      )
      .nullish()
      .optional(),

    insured_details_application_insurance_benefits: predicamentSchema
      .nullish()
      .optional(),
    living_lift: predicamentSchema.nullish(),
    living_lift_description: AHBfloorSchema(translations).nullish(),
    living_situation: z.nativeEnum(HOME_CARE_SITUATION).nullish(),
    living_situation_at_home: z
      .nativeEnum(AT_HOME_SITUATION)
      .nullish()
      .optional(),

    living_special: z
      .string()
      .max(
        FIELD_LIMITS.DESCRIPTION_LONG,
        createMaxLengthMessage(translations, FIELD_LIMITS.DESCRIPTION_LONG),
      )
      .nullish(),
    patient_birthdate: unixDateSchema.nullish(),
    patient_city: z
      .string()
      .max(
        FIELD_LIMITS.CITY,
        createMaxLengthMessage(translations, FIELD_LIMITS.CITY),
      )
      .nullish(),
    patient_first_name: z
      .string()
      .max(
        FIELD_LIMITS.NAME,
        createMaxLengthMessage(translations, FIELD_LIMITS.NAME),
      )
      .nullish(),
    patient_housenumber: z
      .string()
      .max(
        FIELD_LIMITS.HOUSE_NUMBER,
        createMaxLengthMessage(translations, FIELD_LIMITS.HOUSE_NUMBER),
      )
      .nullish(),

    patient_last_name: z
      .string()
      .max(
        FIELD_LIMITS.NAME,
        createMaxLengthMessage(translations, FIELD_LIMITS.NAME),
      )
      .nullish(),

    patient_phone_number: z
      .string()
      .max(
        FIELD_LIMITS.PHONE,
        createMaxLengthMessage(translations, FIELD_LIMITS.PHONE),
      )
      .nullish(),
    patient_street: z
      .string()
      .max(
        FIELD_LIMITS.STREET,
        createMaxLengthMessage(translations, FIELD_LIMITS.STREET),
      )
      .nullish(),
    patient_zipcode: AHBpostleitzahlSchema(translations).nullish(),
    retirement_earning_capacity_company: z
      .string()
      .max(
        FIELD_LIMITS.DESCRIPTION_SHORT,
        createMaxLengthMessage(translations, FIELD_LIMITS.DESCRIPTION_SHORT),
      )
      .nullish(),
    retirement_pension_applied: predicamentSchema.nullish(),
    retirement_pension_company: z
      .string()
      .max(
        FIELD_LIMITS.DESCRIPTION_SHORT,
        createMaxLengthMessage(translations, FIELD_LIMITS.DESCRIPTION_SHORT),
      )
      .nullish(),
    retirement_pension_earning_capacity_applied: predicamentSchema
      .nullish()
      .optional(),

    // Guardian fields (dynamically generated)
    ...createGuardianFields(translations),

    case_id: z
      .string()
      .min(1, createMinLengthMessage(translations, 1))
      .max(
        FIELD_LIMITS.CASE_ID,
        createMaxLengthMessage(translations, FIELD_LIMITS.CASE_ID),
      )
      .nullish()
      .optional(),
    communication_in_german_possible: predicamentSchema.nullish(),
    communication_other_language: z
      .string()
      .max(
        FIELD_LIMITS.DESCRIPTION_SHORT,
        createMaxLengthMessage(translations, FIELD_LIMITS.DESCRIPTION_SHORT),
      )
      .nullish()
      .optional(),

    data_transfer_consent_given: predicamentSchema.nullish(),
    declaration_of_intent_confirmed: isRedesignActive
      ? z
          .boolean()
          .refine((val) => val === true, {
            message: translations.patientForms.fieldRequiredValidation,
          })
          .nullish()
          .optional()
      : z
          .preprocess((raw) => {
            // if it's exactly the string "1", coerce to number 1
            if (
              typeof raw === "string" &&
              raw.trim() === PREDICAMENT_STATE_YES.toString()
            ) {
              return PREDICAMENT_STATE_YES;
            }
            return raw;
          }, z.literal(PREDICAMENT_STATE_YES))
          .nullish()
          .optional(),
    prefered_clinic_city: z
      .string()
      .max(
        FIELD_LIMITS.CITY,
        createMaxLengthMessage(translations, FIELD_LIMITS.CITY),
      )
      .nullish(),
    prefered_clinic_contacted: predicamentSchema.nullish(),
    prefered_clinic_contacted_start_date: unixDateSchema.nullish(),
    prefered_clinic_house_number: z
      .string()
      .max(
        FIELD_LIMITS.HOUSE_NUMBER,
        createMaxLengthMessage(translations, FIELD_LIMITS.HOUSE_NUMBER),
      )
      .nullish(),

    prefered_clinic_name: z
      .string()
      .max(
        FIELD_LIMITS.NAME,
        createMaxLengthMessage(translations, FIELD_LIMITS.NAME),
      )
      .nullish(),
    prefered_clinic_phone: z
      .string()
      .max(
        FIELD_LIMITS.PHONE,
        createMaxLengthMessage(translations, FIELD_LIMITS.PHONE),
      )
      .nullish(),

    prefered_clinic_postal_code: z
      .string()
      .max(
        FIELD_LIMITS.ZIPCODE,
        createMaxLengthMessage(translations, FIELD_LIMITS.ZIPCODE),
      )
      .nullish(),
    prefered_clinic_street: z
      .string()
      .max(
        FIELD_LIMITS.STREET,
        createMaxLengthMessage(translations, FIELD_LIMITS.STREET),
      )
      .nullish(),
    reason_for_reha_clinic: z
      .string()
      .max(
        FIELD_LIMITS.DESCRIPTION_LONG,
        createMaxLengthMessage(translations, FIELD_LIMITS.DESCRIPTION_LONG),
      )
      .nullish(),
    // Section II
    site_id_number: siteIdSchema.nullish(),
    social_worker_fax_number: z
      .string()
      .max(
        FIELD_LIMITS.PHONE,
        createMaxLengthMessage(translations, FIELD_LIMITS.PHONE),
      )
      .nullish(),
    social_worker_first_name: z
      .string()
      .max(
        FIELD_LIMITS.NAME,
        createMaxLengthMessage(translations, FIELD_LIMITS.NAME),
      )
      .nullish(),
    social_worker_last_name: z
      .string()
      .max(
        FIELD_LIMITS.NAME,
        createMaxLengthMessage(translations, FIELD_LIMITS.NAME),
      )
      .nullish()
      .optional(),
    social_worker_phone_number: z
      .string()
      .max(
        FIELD_LIMITS.PHONE,
        createMaxLengthMessage(translations, FIELD_LIMITS.PHONE),
      )
      .nullish()
      .optional(),
  });
}

type ValidationRule = {
  conditional?: {
    field: keyof GKVGeneralFormData;
    value: string | number;
  };
  field: keyof GKVGeneralFormData;
  message: string;
};

type GKVGeneralFormValidationRules = ValidationRule[];

/**
 * Basic required fields that are always validated
 */
const createBasicValidationRules = (
  translations: Translations,
): ValidationRule[] => [
  {
    field: "patient_first_name",
    message: translations.patientForms.generalForm.firstNameRequired,
  },
  {
    field: "patient_last_name",
    message: translations.userManagement.addAccountFlow.mandatoryFieldLastName,
  },
  {
    field: "insurance_number",
    message: translations.patientForms.generalForm.insuranceNumberRequired,
  },
  {
    field: "patient_birthdate",
    message: translations.patientForms.generalForm.dateOfBirthRequired,
  },
];

/**
 * Section I validation rules
 */
const createSectionIValidationRules = (
  translations: Translations,
): ValidationRule[] => [
  {
    field: "insured_details_application_insurance_benefits",
    message: translations.patientForms.generalForm.fieldRequired,
  },
  {
    field: "retirement_pension_applied",
    message: translations.patientForms.generalForm.fieldRequired,
  },
  {
    field: "retirement_pension_earning_capacity_applied",
    message: translations.patientForms.generalForm.fieldRequired,
  },
  {
    field: "living_lift_description",
    message: translations.patientForms.generalForm.livingSituationFloorRequired,
  },
  {
    field: "living_lift",
    message: translations.patientForms.generalForm.fieldRequired,
  },
  {
    field: "living_situation",
    message: translations.patientForms.generalForm.fieldRequired,
  },
  {
    field: "domestic_situation_is_safe",
    message: translations.patientForms.generalForm.fieldRequired,
  },
  {
    field: "declaration_of_intent_confirmed",
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    field: "data_transfer_consent_given",
    message: translations.patientForms.generalForm.fieldRequired,
  },
];

/**
 * Section II validation rules
 */
const createSectionIIValidationRules = (
  translations: Translations,
): ValidationRule[] => [
  {
    field: "site_id_number",
    message: translations.patientForms.generalForm.siteIdRequired,
  },
  {
    field: "case_id",
    message: translations.patientForms.generalForm.caseIdRequired,
  },
  {
    field: "social_worker_last_name",
    message: translations.patientForms.generalForm.lastNameRequired,
  },
  {
    field: "social_worker_phone_number",
    message: translations.patientForms.generalForm.socialWorkerPhoneRequired,
  },
  {
    field: "communication_in_german_possible",
    message: translations.patientForms.generalForm.fieldRequired,
  },
  {
    field: "prefered_clinic_contacted",
    message: translations.patientForms.generalForm.fieldRequired,
  },
];

/**
 * Conditional validation rules that depend on other field values
 */
const createConditionalValidationRules = (
  translations: Translations,
): ValidationRule[] => [
  // Practitioner conditional fields
  {
    conditional: {
      field: "general_practitioner_last_name",
      value: FIELD_HAS_VALUE,
    },
    field: "general_practitioner_first_name",
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: "general_practitioner_first_name",
      value: FIELD_HAS_VALUE,
    },
    field: "general_practitioner_last_name",
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: "general_practitioner_last_name",
      value: FIELD_HAS_VALUE,
    },
    field: "general_practitioner_city",
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: "general_practitioner_first_name",
      value: FIELD_HAS_VALUE,
    },
    field: "general_practitioner_city",
    message: translations.patientForms.fieldRequiredValidation,
  },
  // Guardian conditional fields
  {
    conditional: {
      field: `insured_details_guardian_${GUARDIAN_1}_type`,
      value: FIELD_HAS_VALUE,
    },
    field: `insured_details_guardian_${GUARDIAN_1}_first_name`,
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: `insured_details_guardian_${GUARDIAN_1}_type`,
      value: FIELD_HAS_VALUE,
    },
    field: `insured_details_guardian_${GUARDIAN_1}_last_name`,
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: `insured_details_guardian_${GUARDIAN_1}_first_name`,
      value: FIELD_HAS_VALUE,
    },
    field: `insured_details_guardian_${GUARDIAN_1}_last_name`,
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: `insured_details_guardian_${GUARDIAN_1}_last_name`,
      value: FIELD_HAS_VALUE,
    },
    field: `insured_details_guardian_${GUARDIAN_1}_first_name`,
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: `insured_details_guardian_${GUARDIAN_1}_last_name`,
      value: FIELD_HAS_VALUE,
    },
    field: `insured_details_guardian_${GUARDIAN_1}_type`,
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: `insured_details_guardian_${GUARDIAN_1}_first_name`,
      value: FIELD_HAS_VALUE,
    },
    field: `insured_details_guardian_${GUARDIAN_1}_type`,
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: `insured_details_guardian_${GUARDIAN_2}_type`,
      value: FIELD_HAS_VALUE,
    },
    field: `insured_details_guardian_${GUARDIAN_2}_first_name`,
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: `insured_details_guardian_${GUARDIAN_2}_type`,
      value: FIELD_HAS_VALUE,
    },
    field: `insured_details_guardian_${GUARDIAN_2}_last_name`,
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: `insured_details_guardian_${GUARDIAN_2}_first_name`,
      value: FIELD_HAS_VALUE,
    },
    field: `insured_details_guardian_${GUARDIAN_2}_last_name`,
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: `insured_details_guardian_${GUARDIAN_2}_last_name`,
      value: FIELD_HAS_VALUE,
    },
    field: `insured_details_guardian_${GUARDIAN_2}_first_name`,
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: `insured_details_guardian_${GUARDIAN_2}_last_name`,
      value: FIELD_HAS_VALUE,
    },
    field: `insured_details_guardian_${GUARDIAN_2}_type`,
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: `insured_details_guardian_${GUARDIAN_2}_first_name`,
      value: FIELD_HAS_VALUE,
    },
    field: `insured_details_guardian_${GUARDIAN_2}_type`,
    message: translations.patientForms.fieldRequiredValidation,
  },
  // Living conditional fields
  {
    conditional: {
      field: "communication_in_german_possible",
      value: PREDICAMENT_STATE_NO,
    },
    field: "communication_other_language",
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: "living_situation",
      value: HOME_CARE_SITUATION.HOME,
    },
    field: "living_situation_at_home",
    message: translations.patientForms.generalForm.fieldRequired,
  },
  // Clinic contact conditional fields
  {
    conditional: {
      field: "prefered_clinic_contacted",
      value: PREDICAMENT_STATE_YES,
    },
    field: "prefered_clinic_name",
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: "prefered_clinic_contacted",
      value: PREDICAMENT_STATE_YES,
    },
    field: "prefered_clinic_street",
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: "prefered_clinic_contacted",
      value: PREDICAMENT_STATE_YES,
    },
    field: "prefered_clinic_house_number",
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: "prefered_clinic_contacted",
      value: PREDICAMENT_STATE_YES,
    },
    field: "prefered_clinic_postal_code",
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: "prefered_clinic_contacted",
      value: PREDICAMENT_STATE_YES,
    },
    field: "prefered_clinic_city",
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: "prefered_clinic_contacted",
      value: PREDICAMENT_STATE_YES,
    },
    field: "prefered_clinic_phone",
    message: translations.patientForms.fieldRequiredValidation,
  },
  {
    conditional: {
      field: "prefered_clinic_contacted",
      value: PREDICAMENT_STATE_YES,
    },
    field: "prefered_clinic_contacted_start_date",
    message: translations.patientForms.fieldRequiredValidation,
  },
];

/**
 * Validates a single rule against the form data
 */
const validateRule = (
  rule: ValidationRule,
  data: GKVGeneralFormData,
): { message: string; path: (string | number)[] } | null => {
  let shouldValidate = false;
  if (rule.conditional) {
    const conditionalFieldHasValue =
      rule.conditional.value === FIELD_HAS_VALUE &&
      isFilled(data[rule.conditional.field]);

    const conditionalValueMatches =
      data[rule.conditional.field] === rule.conditional.value;

    shouldValidate = conditionalFieldHasValue || conditionalValueMatches;
  } else {
    shouldValidate = true;
  }

  if (shouldValidate && !isFilled(data[rule.field])) {
    return {
      message: rule.message,
      path: [rule.field],
    };
  }

  return null;
};

/**
 * Main validator function that combines all validation rules
 */
function createGKVGeneralFormValidator(
  translations: Translations,
): (
  data: GKVGeneralFormData,
) => { message: string; path: (string | number)[] }[] {
  return (data: GKVGeneralFormData) => {
    const issues: { message: string; path: (string | number)[] }[] = [];

    // Combine all validation rules
    const allRules: GKVGeneralFormValidationRules = [
      ...createBasicValidationRules(translations),
      ...createSectionIValidationRules(translations),
      ...createSectionIIValidationRules(translations),
      ...createConditionalValidationRules(translations),
    ];

    // Validate each rule and collect issues
    allRules.forEach((rule) => {
      const issue = validateRule(rule, data);
      if (issue) {
        issues.push(issue);
      }
    });

    return issues;
  };
}

export function getGKVGeneralFormSchema({
  isRedesignActive,
  skipRequiredValidation,
  translations,
}: {
  skipRequiredValidation?: boolean;
  translations: Translations;
  isRedesignActive: boolean;
}) {
  const baseSchema = createBaseGKVGeneralFormSchema(
    translations,
    isRedesignActive,
  );
  const partialSchema = baseSchema.partial();

  if (skipRequiredValidation) {
    return z.preprocess(stripEmptyFields, partialSchema);
  }

  const validator = createGKVGeneralFormValidator(translations);
  return createSimultaneousSchema(baseSchema, validator);
}
